import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

export interface SearchableSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface Props {
  id?: string;
  ariaLabel: string;
  value: string | number | '';
  options: SearchableSelectOption[];
  onChange: (value: string | number) => void;
  disabled?: boolean;
  searchEnabled: boolean;
  placeholder?: string;
  emptyOptionLabel?: string;
}

const normalize = (text: string) => text.trim().toLowerCase();

/**
 * Select estático com filtro local opcional (listas de catálogo, opções de lista, tipo).
 */
const SearchableSelect: React.FC<Props> = ({
  id,
  ariaLabel,
  value,
  options,
  onChange,
  disabled = false,
  searchEnabled,
  placeholder = 'Digite para filtrar…',
  emptyOptionLabel = 'Selecione',
}) => {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => options.find((item) => String(item.value) === String(value)),
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!searchEnabled) return options;
    const q = normalize(term);
    if (!q) return options;
    return options.filter((item) => normalize(item.label).includes(q));
  }, [options, searchEnabled, term]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!searchEnabled) {
    return (
      <Form.Select
        id={id}
        aria-label={ariaLabel}
        value={value || ''}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? '' : Number.isNaN(Number(raw)) ? raw : Number(raw));
        }}
      >
        <option value="">{emptyOptionLabel}</option>
        {options.map((item) => (
          <option key={String(item.value)} value={item.value} disabled={item.disabled}>
            {item.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  const displayValue = open ? term : selected ? selected.label : term;

  return (
    <div className="position-relative" ref={wrapRef}>
      <Form.Control
        id={id}
        type="text"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        role="combobox"
        disabled={disabled}
        placeholder={selected && !open ? selected.label : placeholder}
        value={displayValue}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
          if (!e.target.value.trim()) onChange('');
        }}
        onFocus={() => {
          setOpen(true);
          if (selected) setTerm('');
        }}
      />
      {open && filtered.length > 0 ? (
        <div
          className="border rounded-bottom shadow-sm bg-white"
          style={{ maxHeight: 220, overflowY: 'auto', zIndex: 20, position: 'absolute', width: '100%' }}
          role="listbox"
        >
          {filtered.map((item) => (
            <button
              key={String(item.value)}
              type="button"
              role="option"
              disabled={item.disabled}
              className="btn btn-link text-start text-decoration-none text-dark w-100 py-2 px-3 border-bottom rounded-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(item.value);
                setTerm('');
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
      {open && term.trim() && filtered.length === 0 ? (
        <Form.Text className="text-muted d-block mt-1">Nenhum resultado para &quot;{term.trim()}&quot;.</Form.Text>
      ) : null}
    </div>
  );
};

export default SearchableSelect;
